import {
  FooterDataDto,
  TableConfigDto,
} from '@asset-metrix/portal-gateway-client-js';
import { useQuery } from '@tanstack/react-query';
import merge from 'lodash/merge';

import {
  fetchFooterData,
  FetchFooterDataQueryKey,
} from '../../api/portalGateway/footerData';
import { useFilterSelections } from '../../store/hooks/useFilterSelections';
import { prependRoleId } from '../../utils/prependRoleId';

type FooterData = {
  isLoading: boolean;
  data?: FooterDataDto;
};

export type UseFooterParams = {
  widgetId: string;
  tableConfig: TableConfigDto;
  pageFilterInstanceId: string;
  widgetFilterInstanceId: string;
  titleColumnKey: string | undefined;
  shouldRequestFooterData: boolean;
};

export function useFooter({
  widgetId,
  tableConfig,
  pageFilterInstanceId,
  widgetFilterInstanceId,
  titleColumnKey,
  shouldRequestFooterData,
}: UseFooterParams): FooterData {
  const pageFilterSelections = useFilterSelections(pageFilterInstanceId);
  const tableFilterSelections = useFilterSelections(widgetFilterInstanceId);

  const { data: irrFooterData, isPending: isPendingIrrFooter } = useQuery({
    queryKey: prependRoleId<FetchFooterDataQueryKey>([
      'footerDataIrr',
      widgetId,
      pageFilterSelections,
      tableFilterSelections,
      'IRR_ONLY',
    ]),
    queryFn: fetchFooterData,
    enabled:
      (tableConfig.footer != null || tableConfig.isGroupedTable) &&
      shouldRequestFooterData,
  });

  const { data: nonIrrFooterData, isPending: isPendingNonIrrFooter } = useQuery(
    {
      queryKey: prependRoleId<FetchFooterDataQueryKey>([
        'footerDataNonIrr',
        widgetId,
        pageFilterSelections,
        tableFilterSelections,
        'NON_IRR',
      ]),
      queryFn: fetchFooterData,
      enabled:
        (tableConfig.footer != null || tableConfig.isGroupedTable) &&
        shouldRequestFooterData,
    }
  );

  const footerTitle = tableConfig.footer?.footerTitle || 'Total';

  const data =
    irrFooterData || nonIrrFooterData
      ? merge(irrFooterData, nonIrrFooterData)
      : undefined;

  return {
    isLoading: isPendingIrrFooter || isPendingNonIrrFooter,
    data: {
      ...(tableConfig.footer != null &&
        titleColumnKey && {
          columns: {
            ...data?.data.columns,
            [titleColumnKey]: footerTitle,
          },
        }),
      ...(tableConfig.isGroupedTable && { groups: data?.data.groups }),
    },
  };
}
